import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseHttpService } from '@core/http/base-http.service';
import { transformToTagModaByKey } from '@shared/components/tag-field/tag-field.component';
import { EntityName } from '@shared/enums/entity-name.enum';
import { MetaInformation } from '@shared/interfaces/attribute-description.interface';
import { Category } from '@shared/interfaces/category.interface';
import {
  OrganizationInterface,
  OrganizationPrivacy,
  WebsiteMetaData,
} from '@shared/interfaces/organization.interface';
import { ApiGetResponse } from '@shared/interfaces/responses/ApiResponse.interface';
import { StartPageSettings } from '@shared/interfaces/start-page.interface';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class OrganizationHttpService extends BaseHttpService<OrganizationInterface> {
  constructor(protected http: HttpClient) {
    super(http);
  }

  create(
    dto: OrganizationInterface & { entityCreateFromOrgId?: number },
    params: any = {}
  ): Observable<OrganizationInterface> {
    if (!dto) {
      return of(null);
    }
    let url = `${this.apiUrl}/${dto.entityCreateFromOrgId ?? ''}`;
    url = url.replace(/\/$/, ''); // Remove trailing slash

    return this.http.post<OrganizationInterface>(url, dto, { params });
  }

  getAttributeDescription(params): Observable<MetaInformation> {
    return super.getAttributeDescription(params).pipe(
      map(data => {
        return data;
      })
    );
  }

  getApiRootPath(): string {
    return 'organizations';
  }

  gets(params = null): Observable<OrganizationInterface[]> {
    return super.gets(params).pipe(
      map((pagedList: unknown) => {
        return (pagedList as ApiGetResponse<OrganizationInterface>).items.map(
          (org: OrganizationInterface) => this.tranformData(org)
        );
      })
    );
  }

  read(
    id: number,
    headers?: HttpHeaders,
    params?: HttpParams
  ): Observable<OrganizationInterface> {
    return super
      .read(id, headers, params)
      .pipe(map(org => this.tranformData(org)));
  }

  export(
    params: any = null,
    _innerPath: string | null = null,
    _queryParam = null
  ) {
    if (params) {
      const url = `${this.apiUrl}/exports`;
      return this.http.post(url, params, {
        responseType: 'blob',
        observe: 'response',
      });
    }
  }

  paginate(
    params: HttpParams = null,
    innerPath: string | null = null
  ): Observable<ApiGetResponse<OrganizationInterface>> {
    return super.paginate(params, innerPath).pipe(
      map((res: ApiGetResponse<OrganizationInterface>) => {
        return super.handlePaginateResponse(res);
      })
    );
  }

  paginateX(
    params: any,
    innerPath: string | null = null
  ): Observable<ApiGetResponse<OrganizationInterface>> {
    return super.paginateX(params, innerPath).pipe(
      map((res: ApiGetResponse<OrganizationInterface>) => {
        return super.handlePaginateResponse(res);
      })
    );
  }

  checkPartnerOrganizationTeam(orgChallengeId: number): Observable<boolean> {
    if (orgChallengeId && !Number.isNaN(orgChallengeId)) {
      const url = `${this.apiUrl}/check-partner-organization-team`;
      return this.http.get<boolean>(url, {
        params: { id: orgChallengeId.toString() },
      });
    }
    return of(false);
  }

  tranformData(org: OrganizationInterface): OrganizationInterface {
    super.tranformData(org);
    if (org) {
      org.expertisesTag = this.getExpertiesTags(org);
      org.keyOfferingTags = this.transformKeyOfferingTags(org);
    }
    return org;
  }

  paginateAsPost(
    params: any = null,
    innerPath: string | null = null,
    queryParam = null
  ): Observable<ApiGetResponse<OrganizationInterface>> {
    return super.paginateAsPost(params, innerPath, queryParam).pipe(
      map((res: ApiGetResponse<OrganizationInterface>) => {
        return this.handlePaginateResponse(res);
      })
    );
  }

  private getExpertiesTags(
    item: OrganizationInterface
  ): { text: string; weight: number }[] {
    if (item?.expertises) {
      return item.expertises.map(expertise => {
        return {
          text: expertise.description,
          weight: expertise.priority + 10,
        };
      });
    }
    return [];
  }

  private transformKeyOfferingTags(
    item: OrganizationInterface
  ): { display: string; value: number; readonly?: boolean }[] {
    if (item.keyOfferings) {
      return transformToTagModaByKey(
        item.keyOfferings,
        true,
        'description',
        'description'
      );
    }
    return [];
  }

  checkDuplicateName(name: string): Observable<unknown> {
    const url = `${this.apiUrl}/check-org-name-exist`;
    return this.http.get(url, {
      params: {
        name,
      },
    });
  }

  getPartnerOrgs(params): Observable<ApiGetResponse<OrganizationInterface>> {
    const url = `partner-org`;

    return this.paginateX(params, url);
  }

  getPartners(
    id: number,
    params: HttpParams
  ): Observable<ApiGetResponse<OrganizationInterface>> {
    return this.paginateByEntity('/partners', id, params).pipe(
      map((res: ApiGetResponse<OrganizationInterface>) =>
        this.handlePaginateResponse(res)
      )
    );
  }

  deactivateOrg(id) {
    const url = `${this.apiUrl}/${id}/deactivate`;

    return this.http.delete(url);
  }

  updatePartnerOrganizations(id, payload) {
    const url = `${this.apiUrl}/${id}/update-partner-org`;

    return this.http.post(url, payload);
  }

  getPopularOrgs(params) {
    const url = `${this.apiUrl}/popular-org`;
    return this.http.post(url, params);
  }

  updateOrgCommunity(id, payload) {
    const url = `${this.apiUrl}/${id}/update-org-community`;
    return this.http.post(url, payload);
  }

  checkHiddenOrg(id: number): Observable<boolean> {
    const url = `${this.apiUrl}/${id}/check-hidden`;
    return this.http.get<boolean>(url);
  }

  getCategories(id: number, entityName?: EntityName): Observable<Category[]> {
    const url = `${this.apiUrl}/${id}/categories`;
    const params = entityName ? { entityName } : {};

    return this.http.get<Category[]>(url, { params });
  }

  getCategoriesByCategoryId(
    categoryIds: number[],
    entityName: EntityName
  ): Observable<Category[]> {
    const url = `${this.apiUrl}/categories`;

    let params = new HttpParams().set('EntityName', entityName);

    for (const id of categoryIds) {
      params = params.append('CategoryIds', id.toString());
    }

    return this.http.get<Category[]>(url, { params });
  }

  getOrgMetaData(orgUrl: string): Observable<WebsiteMetaData> {
    const url = `${this.apiUrl}/website-metadata`;

    return this.http.get<WebsiteMetaData>(url, {
      params: {
        url: orgUrl,
      },
    });
  }

  checkOrganizationRole(
    organizationId: number,
    isAdmin: boolean
  ): Observable<boolean> {
    const url = `${this.apiUrl}/${organizationId}/check-community`;

    return this.http.get<boolean>(url, {
      params: { isAdmin },
    });
  }

  checkOrgPrivacy(id: number): Observable<OrganizationPrivacy> {
    const url = `${this.apiUrl}/${id}/check-org-privacy`;

    return this.http.get<OrganizationPrivacy>(url);
  }

  getLiteOrganizations(
    orgIds: number[]
  ): Observable<Partial<OrganizationInterface>[]> {
    const url = `${this.apiUrl}/lite`;

    return this.http.post<Partial<OrganizationInterface>[]>(url, {
      orgIds,
    });
  }

  checkCanCreateEvent(orgId: number) {
    const url = `${this.apiUrl}/${orgId}/can-create-event`;

    return this.http.get<boolean>(url);
  }

  saveStartPage(
    organizationId: number,
    payload: Record<string, StartPageSettings>
  ): Observable<void> {
    const url = `${this.apiUrl}/${organizationId}/start-page`;

    return this.http.put<void>(url, payload);
  }
}

export const ORGANIZATION_CUSTOM_ATTRIBUTE_ORDER_OF_PROPERTY_NAMES = [
  'ResidentOf',
  'InnovationFocus',
  'Competency',
  'CompanyType',
  'CountryOfOrigin',
];
